// oauth-web.js — OAuth 2.0 authorisation-code-with-PKCE flow.
//
// Implementation per platform:
//   Web:      window.open → redirect to /oauth-callback.html → postMessage
//             the code back to this window.
//   Electron: window.electronAPI.openOAuthFlow(authUrl) opens a child
//             BrowserWindow and resolves with the code.
//   iOS:      window.Platform.calendar.openOAuth(authUrl) wraps
//             ASWebAuthenticationSession via the capacitor-oauth plugin.
//
// In all three cases the code → token exchange happens server-side in the
// Supabase oauth-exchange edge function so the client_secret never reaches
// the client. The returned token set is written to the workspace vault
// keyed by `oauth:<provider>:<sub>` and an account is registered in the
// provider registry.

import { CryptoEngine } from '../../encryption/crypto-engine.js';
import { uint8ToBase64 } from '../../utils/binary.js';
import { WorkspaceSessionVault } from '../../workspaces/workspace-session-vault.js';
import { ProviderRegistry } from '../provider-registry.js';
import { supabaseFunctionsBase } from './_supabase-base.js';

// Per-provider config. The client_id values must be supplied at build
// time via window.CALENDAR_OAUTH_CONFIG so this module stays
// environment-neutral. The oauth-exchange edge function knows the
// matching client_secret; this client never sees it.
/**
 * @param {string} provider
 * @returns {any} the per-provider OAuth config, or null.
 */
function _cfg(provider: any) {
  const all = (typeof window !== 'undefined' && window.CALENDAR_OAUTH_CONFIG) || {};
  return all[provider] || null;
}

// ── PKCE helpers ──
// Use the chunk-safe base64 codec (utils/binary.js) rather than
// `btoa(String.fromCharCode(...spread))`, whose argument spread overflows the
// call stack (RangeError) on large buffers. Then apply the URL-safe alphabet.
/**
 * @param {ArrayBuffer | Uint8Array} buf
 * @returns {string} URL-safe base64 (no padding).
 */
function _b64url(buf: any) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return uint8ToBase64(bytes).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}
/** @returns {Promise<{ verifier: string, challenge: string, method: string }>} */
async function _pkce() {
  const verifier = _b64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await CryptoEngine.sha256Bytes(new TextEncoder().encode(verifier));
  return { verifier, challenge: _b64url(digest), method: 'S256' };
}

// Build the IDP authorise URL. Returns { url, state, verifier }.
/**
 * @param {string} provider
 * @param {string} redirect
 * @param {any} pkce - { challenge, method }
 * @param {string} state
 * @returns {string} the IDP authorise URL.
 */
function _buildAuthUrl(provider: any, redirect: any, pkce: any, state: any) {
  const cfg = _cfg(provider);
  if (!cfg?.client_id) {
    throw new Error(`OAuth not configured for ${provider} — set window.CALENDAR_OAUTH_CONFIG`);
  }
  const params = new URLSearchParams({
    client_id: cfg.client_id,
    redirect_uri: redirect,
    response_type: 'code',
    scope: cfg.scope,
    code_challenge: pkce.challenge,
    code_challenge_method: pkce.method,
    state,
    access_type: 'offline',
    prompt: 'consent',
  });
  return `${cfg.authorise_url}?${params.toString()}`;
}

// Default redirects per platform. The web one points at our static
// callback page; Electron uses a loopback URL (the main process spins
// up a tiny HTTP listener); iOS uses the custom-scheme deep link.
/**
 * @param {string} provider
 * @returns {string} the platform-appropriate redirect URI.
 */
function _redirectFor(provider: any) {
  const cfg = _cfg(provider) || {};
  if (cfg.redirect_uri) return cfg.redirect_uri;
  const P = window.Platform || {};
  if (P.isElectron) return 'http://127.0.0.1:48817/oauth-callback';
  if (P.isCapacitor) return 'ariadnenotes://oauth/callback';
  return new URL('/oauth-callback.html', location.origin).toString();
}

// Drive the IDP authorise step on the matching platform; resolve with
// { code, state }. All three platforms (web popup, Electron child
// window, iOS ASWebAuthenticationSession) are implemented behind
// Platform.calendar.openOAuth — keep this thin so a future provider
// doesn't have to know which back-end it's talking to.
/**
 * @param {string} provider
 * @param {string} authUrl
 * @returns {Promise<any>} { code, state }
 */
async function _runAuth(_provider: any, authUrl: any) {
  const P = window.Platform || {};
  if (P.calendar && typeof P.calendar.openOAuth === 'function') {
    return await P.calendar.openOAuth(authUrl);
  }
  // Last-ditch fallback: Platform hasn't loaded yet (unusual — would
  // only happen if oauth-web.js were called before platform.js
  // initialises). Surface a clear error rather than silently failing.
  throw new Error('Platform.calendar.openOAuth not available');
}

// Exchange code → token set through the edge function. Returns the
// server's response merged with `expires_at` so the rest of the stack
// can short-circuit refresh.
/**
 * @param {string} provider
 * @param {string} code
 * @param {string} verifier
 * @param {string} redirect
 * @returns {Promise<any>} the token set (with expires_at).
 */
async function _exchange(provider: any, code: any, verifier: any, redirect: any) {
  const base = supabaseFunctionsBase();
  if (!base) throw new Error('SUPABASE_URL not configured');
  const jwt =
    (WorkspaceSessionVault?.getCurrentAccessToken &&
      (await WorkspaceSessionVault.getCurrentAccessToken().catch(() => null as any))) ||
    null;
  const resp = await fetch(`${base}/oauth-exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({
      op: 'exchange',
      provider,
      code,
      code_verifier: verifier,
      redirect_uri: redirect,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`exchange failed: ${resp.status} ${text.slice(0, 200)}`);
  }
  const next = await resp.json();
  return {
    ...next,
    expires_at: next.expires_at || Date.now() + (next.expires_in || 3600) * 1000,
  };
}

// Public entry: drive the full flow for a provider kind. Returns
// { accountId } on success; null if the user cancels.
/**
 * Drive the full OAuth flow for a provider kind.
 * @param {string} provider
 * @returns {Promise<{ accountId: string } | null>} null if the user cancels.
 */
async function connect(provider: any) {
  const pkce = await _pkce();
  const redirect = _redirectFor(provider);
  const state = _b64url(crypto.getRandomValues(new Uint8Array(16)));
  const url = _buildAuthUrl(provider, redirect, pkce, state);

  const auth = await _runAuth(provider, url);
  if (!auth?.code) return null;
  if (auth.state !== state) throw new Error('OAuth state mismatch');

  const tokens = await _exchange(provider, auth.code, pkce.verifier, redirect);
  // The edge function returns the IDP-provided user identifier in `sub`
  // so we can form a stable accountId without a separate userinfo call.
  if (!tokens.sub) throw new Error('exchange did not return sub');
  const accountId = `${provider}:${tokens.sub}`;
  const credentialKey = accountId;
  await WorkspaceSessionVault.save(credentialKey, JSON.stringify(tokens));
  await ProviderRegistry.add({
    accountId,
    provider,
    label: tokens.email || tokens.sub,
    credentialKey,
  });
  await ProviderRegistry.reload();
  return { accountId };
}

// Phase 4.7: real ESM. `CalendarOAuth` is imported directly by the settings
// panel (calendars.js); no window mirror is needed (no classic-script reader).
export const CalendarOAuth = { connect };
